import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Eye, Star, Tag } from "lucide-react";
import { withUploadedImageCacheBust } from "../../core/imageMedia";
import { formatSkuBadgeText, formatVendorBadgeLabel, resolveVendorKeyForBadge } from "../../core/vendorDisplay";

/* =============================================
   PRODUCT CARD — Saree ecommerce card widget
   Template-driven from JSON cardTemplate config
   ============================================= */
export default function ProductCard({ item, onClick }) {
  const PRODUCT_VIEWS_KEY = "lfx_product_views";
  const parseVariantGalleryMap = (value) => {
    if (value && typeof value === "object" && !Array.isArray(value)) return value;
    if (typeof value === "string" && value.trim()) {
      try {
        const parsed = JSON.parse(value);
        return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
      } catch (e) {
        return {};
      }
    }
    return {};
  };
  const toBool = (v) => {
    if (v === true || v === 1) return true;
    const s = String(v ?? "").trim().toLowerCase();
    return s === "true" || s === "1" || s === "yes" || s === "on";
  };
  const isWithinWeek = (dt) => {
    if (!dt) return false;
    const d = new Date(dt);
    if (Number.isNaN(d.getTime())) return false;
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    return diff >= 0 && diff <= 7 * 24 * 60 * 60 * 1000;
  };
  const normalizeUrls = (value) => {
    if (Array.isArray(value)) return value.map((v) => String(v || "").trim()).filter(Boolean);
    if (typeof value === "string" && value.trim()) {
      const raw = value.trim();
      if (raw.startsWith("[") && raw.endsWith("]")) {
        try {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) return parsed.map((v) => String(v || "").trim()).filter(Boolean);
        } catch (e) {
          // fall through
        }
      }
      if (raw.includes(",")) return raw.split(",").map((v) => v.trim()).filter(Boolean);
      return [raw];
    }
    return [];
  };

  const colorOptions = useMemo(() => {
    if (Array.isArray(item?.availableColors)) return item.availableColors.filter(Boolean);
    if (typeof item?.availableColors === "string") {
      return item.availableColors.split(",").map((v) => v.trim()).filter(Boolean);
    }
    return [];
  }, [item]);

  const colorImageMap = useMemo(() => {
    if (item?.colorImageMap && typeof item.colorImageMap === "object" && !Array.isArray(item.colorImageMap)) {
      return item.colorImageMap;
    }
    if (typeof item?.colorImageMap === "string" && item.colorImageMap.trim()) {
      try {
        return JSON.parse(item.colorImageMap);
      } catch (e) {
        return {};
      }
    }
    return {};
  }, [item]);

  const imageList = useMemo(() => {
    const urls = [];
    if (item?.images2) urls.push(item.images2);
    if (item?.mainImage) urls.push(item.mainImage);
    if (item?.thumbnail) urls.push(item.thumbnail);
    if (item?.thumbnailUrl) urls.push(item.thumbnailUrl);
    if (item?.image) urls.push(item.image);
    urls.push(...normalizeUrls(item?.images));
    const variantMap = parseVariantGalleryMap(item?.variantGalleryMap);
    Object.values(variantMap).forEach((shots) => {
      if (!shots || typeof shots !== "object") return;
      Object.values(shots).forEach((u) => {
        if (typeof u === "string" && u.trim()) urls.push(u.trim());
      });
    });
    if (item?.colorImageMap && typeof item.colorImageMap === "object" && !Array.isArray(item.colorImageMap)) {
      Object.values(item.colorImageMap).forEach((u) => {
        if (typeof u === "string" && u.trim()) urls.push(u.trim());
      });
    }
    return [...new Set(urls.filter(Boolean))];
  }, [item]);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [selectedColor, setSelectedColor] = useState("");
  const [fallbackIndex, setFallbackIndex] = useState(0);
  useEffect(() => {
    setFallbackIndex(0);
  }, [item?.id, item?.sku, item?.updatedAt, item?.uploadedAt]);
  const mappedColorImage = selectedColor ? colorImageMap?.[selectedColor] : "";
  const primaryImageRaw = mappedColorImage || imageList[activeImageIndex + fallbackIndex] || "";
  const primaryImage = withUploadedImageCacheBust(primaryImageRaw, item);
  const hasMultipleImages = imageList.length > 1;
  const vendorKey = resolveVendorKeyForBadge(String(item?.vendorId || "").trim(), [primaryImageRaw, imageList[0]]);
  const vendorBadge = formatVendorBadgeLabel(vendorKey);
  const skuBadge = formatSkuBadgeText(item?.sku, item?.id);

  const showPrev = (e) => {
    e.stopPropagation();
    setSelectedColor("");
    setActiveImageIndex((idx) => (idx - 1 + imageList.length) % imageList.length);
  };

  const showNext = (e) => {
    e.stopPropagation();
    setSelectedColor("");
    setActiveImageIndex((idx) => (idx + 1) % imageList.length);
  };
  const discount = item.price && item.discountPrice
    ? Math.round(((item.price - item.discountPrice) / item.price) * 100)
    : 0;
  const offerTypeNorm = String(item.offerType || "").trim().toLowerCase();
  const offerOn = toBool(item.offerActive) || (item.offerActive === undefined || item.offerActive === null || item.offerActive === "");
  const offerPercentNum = Number(item.offerPercent);
  const flatDiscountNum = Number(item.flatDiscount);
  const buyNum = Number(item.buyQty);
  const getNum = Number(item.getQty);
  const isBuyXGetY = (offerTypeNorm === "buyxgety" || offerTypeNorm === "buy x get y") && offerOn;
  const isFlatOffer = offerTypeNorm === "flat" && offerOn;
  const isPercentOffer = offerTypeNorm === "percent" && offerOn;
  const buyXGetYLabel = String(item.offerLabel || "").trim() || `BUY ${Number.isFinite(buyNum) && buyNum > 0 ? buyNum : 2} GET ${Number.isFinite(getNum) && getNum > 0 ? getNum : 1}`;
  const flatOfferLabel = String(item.offerLabel || "").trim() || `FLAT ₹${Number.isFinite(flatDiscountNum) && flatDiscountNum > 0 ? flatDiscountNum : 500} OFF`;
  const effectivePercent = discount > 0
    ? discount
    : (isPercentOffer && Number.isFinite(offerPercentNum) && offerPercentNum > 0 ? Math.round(offerPercentNum) : 0);
  const savings = item.price && item.discountPrice ? Math.max(0, Number(item.price) - Number(item.discountPrice)) : 0;
  const latestByDate = isWithinWeek(item.uploadedAt || item.generatedAt || item.updatedAt);
  const showLatest = toBool(item.isLatest) || latestByDate;
  const stockText = String(item.stock || "").toLowerCase();
  const statusText = String(item.stockStatus || "").toLowerCase();
  const stockNum = Number(item.stockCount);
  const isOut = toBool(item.outOfStock) || statusText === "out of stock" || stockText.includes("out of stock") || (Number.isFinite(stockNum) && stockNum <= 0);
  const isLimited = !isOut && ((Number.isFinite(stockNum) && stockNum > 0 && stockNum < 5) || stockText.includes("limited"));
  const stockBadge = isOut ? "Out of Stock" : isLimited ? "Limited Stock" : "";
  const stockBadgeClass = isOut ? "out" : isLimited ? "limited" : "";
  const viewCount = useMemo(() => {
    const sku = String(item?.sku || "").trim().toLowerCase();
    const id = String(item?.id || "").trim().toLowerCase();
    const token = sku ? `sku:${sku}` : (id ? `id:${id}` : "");
    let tracked = NaN;
    if (token && typeof window !== "undefined") {
      try {
        const raw = localStorage.getItem(PRODUCT_VIEWS_KEY);
        const parsed = raw ? JSON.parse(raw) : {};
        tracked = Number(parsed?.[token]);
      } catch (e) {
        tracked = NaN;
      }
    }
    const base = Number(item?.views);
    if (Number.isFinite(tracked)) return tracked;
    return Number.isFinite(base) ? base : 0;
  }, [item?.id, item?.sku, item?.views]);

  return (
    <div className="lfx-product-card" onClick={onClick}>
      {/* IMAGE */}
      <div className="lfx-product-img-wrap">
        {primaryImage ? (
          <img
            key={`${primaryImageRaw}|${item?.updatedAt || item?.uploadedAt || ""}`}
            src={primaryImage}
            alt={item.name}
            className="lfx-product-img"
            loading="lazy"
            onError={() => {
              if (mappedColorImage) {
                setSelectedColor("");
                setFallbackIndex(0);
                return;
              }
              setFallbackIndex((n) => (activeImageIndex + n + 1 < imageList.length ? n + 1 : n));
            }}
          />
        ) : (
          <div className="lfx-product-img-placeholder">
            <span>🥻</span>
          </div>
        )}
        {hasMultipleImages && (
          <>
            <button
              type="button"
              onClick={showPrev}
              className="lfx-product-img-nav lfx-product-img-nav--prev"
              aria-label="Previous image"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              type="button"
              onClick={showNext}
              className="lfx-product-img-nav lfx-product-img-nav--next"
              aria-label="Next image"
            >
              <ChevronRight size={16} />
            </button>
          </>
        )}
        {isBuyXGetY ? (
          <span className="lfx-offer-badge">{buyXGetYLabel}</span>
        ) : isFlatOffer ? (
          <span className="lfx-offer-badge">{flatOfferLabel}</span>
        ) : effectivePercent > 0 && (
          <span className="lfx-discount-badge">{effectivePercent}% OFF</span>
        )}
        {showLatest ? <span className="lfx-latest-badge">Latest</span> : null}
        {stockBadge ? <span className={`lfx-stock-badge ${stockBadgeClass}`}>{stockBadge}</span> : null}
        <span className={`lfx-category-pill ${(item.category || "").toLowerCase()}`}>
          {item.category}
        </span>
        {(vendorBadge || skuBadge) ? (
          <div className="lfx-product-thumb-codes">
            {vendorBadge ? (
              <span className="lfx-vendor-thumb-badge">
                {vendorBadge}
              </span>
            ) : null}
            {skuBadge ? (
              <span className="lfx-sku-thumb-badge">
                {skuBadge}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* INFO */}
      <div className="lfx-product-info">
        <div className="lfx-product-name">{item.name}</div>
        <div className="lfx-savings-text" style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Eye size={12} />
          <span>{viewCount} views</span>
        </div>
        {colorOptions.length > 0 && (
          <div className="lfx-color-row">
            <span className="lfx-color-label">Colors:</span>
            <div className="lfx-color-swatches">
              {colorOptions.map((color) => (
                <button
                  type="button"
                  key={color}
                  className={`lfx-color-chip ${selectedColor === color ? "active" : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedColor(color);
                    const mapped = colorImageMap?.[color];
                    if (!mapped) setSelectedColor("");
                  }}
                  title={color}
                >
                  {color}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* RATING */}
        {item.rating && (
          <div className="lfx-rating">
            {[1, 2, 3, 4, 5].map((s) => (
              <Star
                key={s}
                size={12}
                className={s <= Math.round(item.rating) ? "star-filled" : "star-empty"}
              />
            ))}
            <span className="lfx-rating-val">{item.rating}</span>
          </div>
        )}

        {/* PRICE */}
        <div className="lfx-price-row">
          <span className="lfx-offer-price">₹{Number(item.discountPrice).toLocaleString("en-IN")}</span>
          {item.price && item.price !== item.discountPrice && (
            <span className="lfx-mrp">₹{Number(item.price).toLocaleString("en-IN")}</span>
          )}
        </div>
        {savings > 0 ? (
          <div className="lfx-savings-text">Save ₹{savings.toLocaleString("en-IN")}</div>
        ) : null}
      </div>
    </div>
  );
}
